import CopyText from '@/pages/Auth/components/CopyText';
import { Button } from '@nautilusbio/nb-design-system/Button';
import { Popover, type PopoverProps } from '@nautilusbio/nb-design-system/Popover';
import { Stack } from '@nautilusbio/nb-design-system/Stack';
import { Text } from '@nautilusbio/nb-design-system/Text';
import React, { useState } from 'react';

// Supports { name?: string, email?: string } (allowing null/undefined handling)
export interface StudyContactCellProps {
  id: string;
  value?: {
    name?: string | null;
    email?: string | null;
  } | null;
}

const popoverPositions: PopoverProps['positions'] = ['top', 'bottom'];
const popoverPadding = 8;
const fallback = <Text as="span">—</Text>;
/**
 * Study Contact cell for studies table. If name is present, displays name, popover shows email & copy button.
 * If only email, shows inline with copy; if neither, shows '—'.
 */

const StudyContactCell = ({ id, value }: StudyContactCellProps) => {
  const [open, setOpen] = useState(false);
  const onClickOutsideHandler = () => setOpen(false);
  const popoverBtnOnPressHandler = () => setOpen(prev => !prev);

  if (!value || (!value.name && !value.email)) {
    return fallback;
  }

  if (value.name) {
    const content = value.email ? (
      <Stack orientation="horizontal" gap={8} alignItems="center">
        <CopyText id={`it-contact-${id}`} value={value.email} />
      </Stack>
    ) : (
      <Text as="span" size="sm">
        No email.
      </Text>
    );
    return (
      <Popover
        content={content}
        isOpen={open}
        onClickOutside={onClickOutsideHandler}
        padding={popoverPadding}
        positions={popoverPositions}
        styled
      >
        <Button
          aria-haspopup="dialog"
          aria-expanded={open}
          data-testid={`popover-btn-${id}`}
          onPress={popoverBtnOnPressHandler}
          size="sm"
          tabIndex={0}
        >
          {value.name}
        </Button>
      </Popover>
    );
  }
  // name-less, fallback to just email, copyable
  if (value.email) {
    return <CopyText id={`it-contact-${id}`} value={value.email} />;
  }

  return fallback;
};

export default StudyContactCell;
